import axios from "axios";
import { logError } from "../utils/logger";

// Configuração da API do Backend
const API_BASE_URL =
  process.env.REACT_APP_API_BASE_URL || "http://localhost:3001/api";

// Interface para o plano retornado pela API
export interface Plan {
  id: string;
  name: string;
  description?: string;
  currency: string;
  interval: string;
  interval_count: number;
  billing_type: string;
  payment_methods: string[];
  installments: number[];
  status: string;
  created_at: string;
  updated_at: string;
  items: PlanItem[];
}

export interface PlanItem {
  id: string;
  name: string;
  description?: string;
  pricing_scheme: {
    price: number;
    scheme_type: string;
  };
  quantity?: number;
}

export interface PlansResponse {
  data: Plan[];
  paging?: {
    total: number;
    next?: string;
    previous?: string;
  };
}

export interface ApiResponse<T> {
  success: boolean;
  data?: T;
  error?: any;
}

// Cliente HTTP configurado para o Backend
const apiClient = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    "Content-Type": "application/json",
    Accept: "application/json",
  },
  timeout: 15000,
});

/**
 * Converte "cash" (API Pagar.me) para "pix" (Frontend)
 * A API de Planos usa "cash" mas queremos mostrar "pix" para o usuário
 */
const normalizePlanPaymentMethods = (plan: Plan): Plan => {
  return {
    ...plan,
    payment_methods: plan.payment_methods.map((method) =>
      method === "cash" ? "pix" : method,
    ),
  };
};

/**
 * Lista todos os planos disponíveis no Pagar.me
 * @param page - Página a ser buscada (padrão: 1)
 * @param size - Quantidade de planos por página (padrão: 50)
 * @returns Lista de planos ou erro
 */
export const fetchPlans = async (
  page: number = 1,
  size: number = 50,
): Promise<ApiResponse<Plan[]>> => {
  const requestStartTime = Date.now();

  try {
    const response = await apiClient.get<any>("/plans");

    const duration = Date.now() - requestStartTime;
    // Backend retorna: {success: true, data: {data: Plan[], paging: {...}}}
    const plans = response.data?.data?.data || [];

    // Filtra planos deletados e normaliza payment_methods: cash -> pix
    const normalizedPlans = plans
      .filter((p: Plan) => p.status !== "deleted")
      .map(normalizePlanPaymentMethods);

    return {
      success: true,
      data: normalizedPlans,
    };
  } catch (error: any) {
    const duration = Date.now() - requestStartTime;

    logError("Erro ao buscar planos", {
      error: error.response?.data || error.message,
      status: error.response?.status,
      duration_ms: duration,
    });

    return {
      success: false,
      error: error.response?.data || error.message,
    };
  }
};

/**
 * Busca um plano específico pelo ID
 * @param planId - ID do plano
 * @returns Dados do plano ou erro
 */
export const fetchPlanById = async (
  planId: string,
): Promise<ApiResponse<Plan>> => {
  const requestStartTime = Date.now();

  try {
    const response = await apiClient.get<any>(`/plans/${planId}`);

    const duration = Date.now() - requestStartTime;
    // Backend retorna: {success: true, data: Plan}
    const plan = response.data?.data;

    // Normaliza payment_methods: cash -> pix
    const normalizedPlan = plan ? normalizePlanPaymentMethods(plan) : undefined;

    return {
      success: true,
      data: normalizedPlan,
    };
  } catch (error: any) {
    const duration = Date.now() - requestStartTime;

    logError("Erro ao buscar plano", {
      plan_id: planId,
      error: error.response?.data || error.message,
      status: error.response?.status,
      duration_ms: duration,
    });

    return {
      success: false,
      error: error.response?.data || error.message,
    };
  }
};

/**
 * Extrai o preço do plano (de items[0].pricing_scheme.price)
 * @param plan - Objeto do plano
 * @returns Valor em centavos
 */
export const getPlanAmount = (plan: Plan): number => {
  return plan.items?.[0]?.pricing_scheme?.price || 0;
};

/**
 * Formata o valor do plano (centavos para reais)
 * @param amountInCents - Valor em centavos
 * @returns Valor formatado em reais
 */
export const formatPlanPrice = (amountInCents: number): string => {
  const reais = amountInCents / 100;
  return new Intl.NumberFormat("pt-BR", {
    style: "currency",
    currency: "BRL",
  }).format(reais);
};

/**
 * Formata o intervalo de cobrança do plano
 * @param interval - Tipo de intervalo (month, year, day, week)
 * @param intervalCount - Quantidade de intervalos
 * @returns Descrição formatada
 */
export const formatPlanInterval = (
  interval: string,
  intervalCount: number,
): string => {
  const intervalLabels: { [key: string]: string } = {
    day: intervalCount === 1 ? "diária" : "dias",
    week: intervalCount === 1 ? "semanal" : "semanas",
    month: intervalCount === 1 ? "mensal" : "meses",
    year: intervalCount === 1 ? "anual" : "anos",
  };

  const label = intervalLabels[interval] || interval;
  return intervalCount === 1
    ? `Renovação ${label}`
    : `Renovação ${intervalCount} ${label}`;
};
